import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";

const STATUS_STYLES = {
  available: { fill: "#15803D", fillOpacity: 0.22, stroke: "#16A34A", label: "Available" },
  pending: { fill: "#B45309", fillOpacity: 0.22, stroke: "#D97706", label: "Pending" },
  leased: { fill: "#64748B", fillOpacity: 0.38, stroke: "#475569", label: "Leased" },
};

// Match the seeded coordinate system in /app/backend/lots_data.py
const VB_W = 1000;
const VB_H = 820;

// Internal roads — pixels in the SVG viewBox
const WEST_ROAD = { x: 170, y: 15, width: 55, height: 580 };      // vertical
const MAIN_ROAD = { x: 540, y: 15, width: 65, height: 580 };       // vertical
const SOUTH_ROAD = { x: 15, y: 590, width: 970, height: 45 };      // horizontal cul-de-sac

const ROAD_FILL = "#E8E5D8";
const ROAD_STROKE = "#C6C3B5";
const ROAD_DASH = "#FACC15";

export const SitePlanMap = ({ lots }) => {
  const navigate = useNavigate();
  const [hover, setHover] = useState(null);

  const ordered = useMemo(
    () => [...(lots || [])].sort((a, b) => a.lot_number - b.lot_number),
    [lots]
  );

  return (
    <div data-testid="site-plan-map" className="relative w-full">
      <div className="relative border border-[#C6C3B5] bg-white overflow-hidden shadow-sm">
        <svg viewBox={`0 0 ${VB_W} ${VB_H}`} className="w-full h-auto block" style={{ aspectRatio: `${VB_W} / ${VB_H}` }}>
          {/* Paper / property boundary */}
          <rect x="0" y="0" width={VB_W} height={VB_H} fill="#F8F7F1" />
          <rect x="8" y="8" width={VB_W - 16} height={VB_H - 16} fill="#FCFBF6" stroke="#0B0C0E" strokeWidth="2.5" />
          {/* Subtle internal hatching for ground */}
          <rect x="8" y="8" width={VB_W - 16} height={VB_H - 16} fill="url(#hatch)" opacity="0.4" />

          <defs>
            <pattern id="hatch" patternUnits="userSpaceOnUse" width="22" height="22">
              <path d="M-2,4 l8,-8 M0,22 l22,-22 M16,24 l8,-8" stroke="#D9D6C4" strokeWidth="0.6" />
            </pattern>
          </defs>

          {/* 118A Avenue NW header strip */}
          <rect x="8" y="8" width={VB_W - 16} height="18" fill="#0B0C0E" />
          <text x={VB_W / 2} y="22" textAnchor="middle" fill="#FACC15" fontSize="11" fontFamily="IBM Plex Mono" fontWeight="700" letterSpacing="2">
            118A AVENUE NW
          </text>

          {/* Roads */}
          <g>
            {/* West access road */}
            <rect {...WEST_ROAD} fill={ROAD_FILL} stroke={ROAD_STROKE} strokeWidth="1" />
            <line x1={WEST_ROAD.x + WEST_ROAD.width / 2} y1={WEST_ROAD.y + 6} x2={WEST_ROAD.x + WEST_ROAD.width / 2} y2={WEST_ROAD.y + WEST_ROAD.height - 6} stroke={ROAD_DASH} strokeWidth="1" strokeDasharray="8 8" opacity="0.55" />
            {/* Main road */}
            <rect {...MAIN_ROAD} fill={ROAD_FILL} stroke={ROAD_STROKE} strokeWidth="1" />
            <line x1={MAIN_ROAD.x + MAIN_ROAD.width / 2} y1={MAIN_ROAD.y + 6} x2={MAIN_ROAD.x + MAIN_ROAD.width / 2} y2={MAIN_ROAD.y + MAIN_ROAD.height - 6} stroke={ROAD_DASH} strokeWidth="1" strokeDasharray="8 8" opacity="0.55" />
            <text x={MAIN_ROAD.x + MAIN_ROAD.width / 2} y={300} textAnchor="middle" fill="#56606E" fontSize="11" fontFamily="IBM Plex Mono" fontWeight="600" transform={`rotate(-90 ${MAIN_ROAD.x + MAIN_ROAD.width / 2} 300)`}>
              ROAD · 20.0m
            </text>
            {/* South cul-de-sac corridor */}
            <rect {...SOUTH_ROAD} fill={ROAD_FILL} stroke={ROAD_STROKE} strokeWidth="1" />
            <line x1={SOUTH_ROAD.x + 6} y1={SOUTH_ROAD.y + SOUTH_ROAD.height / 2} x2={SOUTH_ROAD.x + SOUTH_ROAD.width - 6} y2={SOUTH_ROAD.y + SOUTH_ROAD.height / 2} stroke={ROAD_DASH} strokeWidth="1" strokeDasharray="8 8" opacity="0.55" />
            <text x={500} y={SOUTH_ROAD.y + 30} textAnchor="middle" fill="#56606E" fontSize="10" fontFamily="IBM Plex Mono" fontWeight="600">
              CUL-DE-SAC
            </text>
          </g>

          {/* Entry & Exit arrows (from 118A Ave) */}
          <g>
            <polygon points={`${WEST_ROAD.x + WEST_ROAD.width / 2 - 8},${30} ${WEST_ROAD.x + WEST_ROAD.width / 2 + 8},${30} ${WEST_ROAD.x + WEST_ROAD.width / 2},${48}`} fill="#15803D" />
            <text x={WEST_ROAD.x + WEST_ROAD.width / 2} y="62" textAnchor="middle" fill="#15803D" fontSize="9" fontFamily="IBM Plex Mono" fontWeight="700">ENTRY</text>

            <polygon points={`${MAIN_ROAD.x + MAIN_ROAD.width / 2 - 8},${48} ${MAIN_ROAD.x + MAIN_ROAD.width / 2 + 8},${48} ${MAIN_ROAD.x + MAIN_ROAD.width / 2},${30}`} fill="#B45309" />
            <text x={MAIN_ROAD.x + MAIN_ROAD.width / 2} y="62" textAnchor="middle" fill="#B45309" fontSize="9" fontFamily="IBM Plex Mono" fontWeight="700">EXIT</text>
          </g>

          {/* North compass */}
          <g transform={`translate(${VB_W - 60}, 70)`}>
            <circle r="22" fill="#FFFFFF" stroke="#0B0C0E" strokeWidth="1.5" />
            <polygon points="0,-15 -7,8 0,3 7,8" fill="#0B0C0E" />
            <text x="0" y="22" textAnchor="middle" fontSize="11" fontFamily="Cabinet Grotesk" fontWeight="900" fill="#0B0C0E">N</text>
          </g>

          {/* Lots */}
          {ordered.map((lot) => {
            const c = STATUS_STYLES[lot.status] || STATUS_STYLES.available;
            const isHover = hover === lot.lot_number;
            const isAvail = lot.status === "available";
            const cx = lot.x + lot.w / 2;
            const cy = lot.y + lot.h / 2;
            return (
              <g
                key={lot.lot_number}
                onMouseEnter={() => setHover(lot.lot_number)}
                onMouseLeave={() => setHover(null)}
                onClick={() => isAvail && navigate(`/lot/${lot.lot_number}`)}
                style={{ cursor: isAvail ? "pointer" : "not-allowed" }}
                data-testid={`lot-${lot.lot_number}`}
              >
                <rect
                  x={lot.x} y={lot.y} width={lot.w} height={lot.h}
                  fill={c.fill}
                  fillOpacity={isHover ? c.fillOpacity + 0.25 : c.fillOpacity}
                  stroke={isHover ? "#0B0C0E" : c.stroke}
                  strokeWidth={isHover ? 2.5 : 1.4}
                  className="lot-rect"
                />
                <text x={cx} y={cy - 6} textAnchor="middle" fontFamily="Cabinet Grotesk" fontWeight="900" fontSize="28" fill="#0B0C0E" pointerEvents="none">
                  {`LOT ${lot.lot_number}`}
                </text>
                <text x={cx} y={cy + 16} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="12" fontWeight="600" fill="#2C2F36" pointerEvents="none">
                  {`${lot.sq_ft.toLocaleString()} sq ft · ${lot.acres.toFixed(2)} ac`}
                </text>
                <text x={cx} y={cy + 32} textAnchor="middle" fontFamily="Cabinet Grotesk" fontSize="14" fontWeight="800" fill="#0B0C0E" pointerEvents="none">
                  {`$${lot.monthly_rent.toLocaleString()}/mo`}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Floating tooltip */}
        {hover && (() => {
          const lot = ordered.find((l) => l.lot_number === hover);
          if (!lot) return null;
          const c = STATUS_STYLES[lot.status];
          return (
            <div
              className="absolute top-3 right-3 bg-white border-2 border-[#0B0C0E] p-4 max-w-[240px] shadow-lg"
              data-testid={`lot-tooltip-${lot.lot_number}`}
            >
              <div className="overline">LOT {lot.lot_number}</div>
              <div className="font-display font-black text-3xl text-[#0B0C0E] mt-1">
                ${lot.monthly_rent.toLocaleString()}
                <span className="text-sm font-medium text-[#56606E]">/mo</span>
              </div>
              <div className="text-xs text-[#56606E] mt-1">{lot.sq_ft.toLocaleString()} sq ft · {lot.acres.toFixed(2)} ac</div>
              <div className="mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide" style={{ color: c.stroke }}>
                <span className="w-2 h-2" style={{ background: c.stroke }} /> {c.label}
              </div>
              {lot.status === "available" && (
                <button onClick={() => navigate(`/lot/${lot.lot_number}`)} className="mt-3 w-full bg-[#FACC15] hover:bg-[#FDE047] text-[#0B0C0E] font-bold py-2 px-3 text-xs uppercase tracking-wide" data-testid={`tooltip-book-${lot.lot_number}`}>
                  Book →
                </button>
              )}
            </div>
          );
        })()}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5 text-xs uppercase tracking-wider text-[#2C2F36] font-semibold" data-testid="map-legend">
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3" style={{ background: STATUS_STYLES.available.stroke }} /> Available</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3" style={{ background: STATUS_STYLES.pending.stroke }} /> Pending Payment</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3" style={{ background: STATUS_STYLES.leased.stroke }} /> Leased</span>
        <span className="text-[#56606E] ml-auto normal-case tracking-normal text-sm">Click any available lot to start →</span>
      </div>
    </div>
  );
};

export default SitePlanMap;
